"use client";

import React from "react";

import Image from "next/image";
import DecorationLine from "@/components/global/ui/DecorationLine";
import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import {PortableTextBlock} from "sanity";
import {GROQImage} from "@/sanity/lib/definitions";
import Chip from "@/components/global/ui/Chip";
import UnicornWrapper from "@/components/global/utils/UnicornWrapper";

import halfCircle from "@/public/icons/half-circle.svg";
import logoFallback from "@/public/global/scrolling-blur-fallback.png";

export default function ScrollingBlurSection({
  chip,
  text,
  items,
  className
}: {
  chip?: {
    icon: GROQImage;
    text: string;
  };
  text?: PortableTextBlock[];
  items: {
    name: string;
  }[];
  className?: string;
}) {
  return (
    <section className={cn(
      "relative bg-[#0A1A1D] overflow-clip",
      className
    )}>
      <Image
        className="!w-[27rem] sm:!w-[48rem] lg:!w-[80rem] absolute top-0 -left-[14rem] lg:left-0"
        src={halfCircle}
        alt=""
      />
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="left"
        style="light"
      />
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="right"
        style="light"
      />

      <div className="relative container lg:max-w-[205.5rem]">
        <div className="flex flex-col lg:flex-row-reverse items-center lg:items-start lg:justify-between gap-[8rem] lg:pr-[8rem]">
          <div className="flex flex-col lg:flex-col-reverse items-center lg:items-start w-[min(73.625rem,100%)] pt-[6rem] lg:pt-[12rem] relative">
            <div className="flex-shrink-0 w-[min(27rem,100%)] sm:w-[min(50rem,100%)] lg:w-full aspect-square relative [&_canvas]:!w-full [&_canvas]:!h-full opacity-[.15] lg:scale-[1.65] lg:origin-top mix-blend-screen lg:absolute lg:top-full lg:left-1/2 lg:-translate-x-1/2">
              <UnicornWrapper
                jsonFilePath="unicorn-studio/logo-interaction.json"
                placeholderImage={logoFallback}
              />
            </div>

            <div className="flex flex-col items-center lg:items-start">
              {chip &&  (
                <Chip
                  className="prose-p:text-[#D9F9B9] prose-p:font-geistmono"
                  image={chip?.icon}
                  text={chip.text}
                  textBG="bg-transparent"
                />
              )}

              {text && (
                <div className="richtext lg:richtext-start prose-em:font-ivy prose-headings:text-white prose-p:px-[2rem] prose-p:lg:px-0 prose-p:text-white/60 prose-headings:w-[min(43rem,100%)] md:prose-headings:w-auto prose-strong:font-normal prose-strong:text-white">
                  <PortableText
                    value={text}
                    components={richTextComponent}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="w-auto max-w-[95%] lg:max-w-[unset] lg:w-[121rem] h-[62.5rem] lg:h-[122.5rem] relative">
            {/* Top gradual blur */}
            <div className="absolute -top-[5rem] lg:top-0 -left-[5rem] -right-[5rem] pointer-events-none z-10 h-[15rem]">
              <div className="relative w-full h-full">
                {/* heaviest */}
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  WebkitBackdropFilter: 'blur(.75rem)',
                  backdropFilter: 'blur(.75rem)',
                  WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 30%, transparent 85%)',
                  maskImage: 'linear-gradient(to bottom, black 0%, black 30%, transparent 85%)'
                }} />
                {/* medium */}
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  WebkitBackdropFilter: 'blur(.5rem)',
                  backdropFilter: 'blur(.5rem)',
                  WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 45%, transparent 95%)',
                  maskImage: 'linear-gradient(to bottom, black 0%, black 45%, transparent 95%)'
                }} />
                {/* light */}
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  WebkitBackdropFilter: 'blur(.25rem)',
                  backdropFilter: 'blur(.25rem)',
                  WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 60%, transparent 100%)',
                  maskImage: 'linear-gradient(to bottom, black 0%, black 60%, transparent 100%)'
                }} />
              </div>
            </div>

            {/* Bottom gradual blur */}
            <div className="absolute bottom-0 -left-[5rem] -right-[5rem] pointer-events-none z-10 h-[10rem] lg:h-[15rem]">
              <div className="relative w-full h-full">
                {/* light */}
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  WebkitBackdropFilter: 'blur(.25rem)',
                  backdropFilter: 'blur(.25rem)',
                  WebkitMaskImage: 'linear-gradient(to top, black 0%, black 60%, transparent 100%)',
                  maskImage: 'linear-gradient(to top, black 0%, black 60%, transparent 100%)'
                }} />
                {/* medium */}
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  WebkitBackdropFilter: 'blur(.5rem)',
                  backdropFilter: 'blur(.5rem)',
                  WebkitMaskImage: 'linear-gradient(to top, black 0%, black 45%, transparent 95%)',
                  maskImage: 'linear-gradient(to top, black 0%, black 45%, transparent 95%)'
                }} />
                {/* heaviest */}
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  WebkitBackdropFilter: 'blur(.75rem)',
                  backdropFilter: 'blur(.75rem)',
                  WebkitMaskImage: 'linear-gradient(to top, black 0%, black 30%, transparent 85%)',
                  maskImage: 'linear-gradient(to top, black 0%, black 30%, transparent 85%)'
                }} />
              </div>
            </div>

            {items && (
              <div className="flex flex-col overflow-hidden">
                <div
                  className="flex flex-col gap-[1.5rem] sm:gap-[3rem] lg:gap-[5rem] pt-[1.5rem] sm:pt-[3rem] lg:pt-[5rem]"
                  style={{
                    animation: `scrollDown ${items.length * 3}s linear infinite`,
                    willChange: 'transform',
                  }}
                >
                  {[...items, ...items, ...items, ...items].map((item, index) => (
                    <div
                      key={index}
                      className="flex-shrink-0"
                    >
                      <p className="scrollingText font-funnel mb-0 font-semibold text-[6rem] sm:text-[10rem] lg:text-[15rem] text-[#D9F9B9] uppercase leading-none">{item?.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
